import { Injectable } from "@angular/core";
import OneSignal from 'onesignal-cordova-plugin';
import { environment } from 'src/environments/environment';
import { Auth } from "src/providers/auth/auth";
import { Me } from "src/providers/me/me";
import { CustomNavController } from "./navigation/custom-nav-controller";
import { AfterLoginRedirect } from "src/providers/auth/afterLoginRedirect";
import { NotificationsRedirectService } from "src/providers/notifications/notifications-redirect";
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class OneSignalNotification {
  private envTag = `fe-ca-${environment.environment}`;
  private isInitialized = false;
  private initRetryCount = 0;
  private readonly maxRetries = 5;
  private readonly retryDelay = 2000;
  private userSetupInProgress = false;
  private currentUserId: number | null = null;
  private subscriptionInfoLogged = false;
  private hasPromptedThisSession = false;
  private readonly permissionPromptedKey = 'oneSignal_permission_prompted';

  constructor(
    private auth: Auth,
    private me: Me,
    private navCtrl: CustomNavController,
    private afterLoginRedirect: AfterLoginRedirect,
    private notificationsRedirect: NotificationsRedirectService
  ) {}

  async initOneSignal() {
    if (!Capacitor.isNativePlatform()) {
      console.warn('OneSignal: Not a native platform, skipping initialization');
      return;
    }

    if (this.isInitialized) {
      console.warn('OneSignal: Already initialized');
      return;
    }


    const oneSignal = (typeof OneSignal !== 'undefined') ? OneSignal : (typeof window !== 'undefined' && (window as any).OneSignal) ? (window as any).OneSignal : null;

    if (!oneSignal) {
      console.error('OneSignal: SDK not loaded. Retrying...', this.initRetryCount);
      if (this.initRetryCount < this.maxRetries) {
        this.initRetryCount++;
        setTimeout(() => this.initOneSignal(), this.retryDelay);
        return;
      } else {
        console.error('OneSignal: Failed to load SDK after max retries');
        return;
      }
    }

    try {
      if (!environment.one_signal_app_id) {
        console.error('OneSignal: App ID not configured in environment');
        return;
      }

      console.warn('OneSignal: Initializing with App ID:', environment.one_signal_app_id);

      oneSignal.initialize(environment.one_signal_app_id);

      if (typeof oneSignal.Notifications === 'undefined') {
        console.error('OneSignal: Notifications API not available');
        console.error('OneSignal: Available APIs:', Object.keys(oneSignal || {}).join(', '));
        return;
      }

      oneSignal.Notifications.addEventListener('click', (event) => {
        console.warn('OneSignal: Notification clicked', event);
        this.notificationOpened(event);
      });

      oneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
        console.warn('OneSignal: Notification received in foreground', event);
      });

      if (typeof oneSignal.Location !== 'undefined') {
        oneSignal.Location.setShared(false);
      }

      if (typeof oneSignal.User !== 'undefined' && typeof oneSignal.User.pushSubscription !== 'undefined') {
        oneSignal.User.pushSubscription.addEventListener('change', (event) => {
          console.warn('OneSignal: Push subscription changed event:', JSON.stringify(event));
          if (!this.subscriptionInfoLogged) {
            this.logSubscriptionInfo(oneSignal);
          }
        });

        // Add user state observer to catch errors
        if (typeof oneSignal.User.addEventListener === 'function') {
          console.warn('OneSignal: Adding user state change listener...');
          oneSignal.User.addEventListener('change', (event) => {
            console.warn('OneSignal: User state changed:', JSON.stringify(event));
          });
        }
      }

      this.isInitialized = true;
      this.alreadyAuthenticatedInit();
    } catch (error) {
      console.error('OneSignal: Initialization error:', error);
      if (this.initRetryCount < this.maxRetries) {
        this.initRetryCount++;
        setTimeout(() => this.initOneSignal(), this.retryDelay);
      }
    }
  }

  async requestPermissionIfNeeded(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    if (this.hasPromptedThisSession) {
      console.warn('OneSignal: Already prompted this session');
      return false;
    }

    if (localStorage.getItem(this.permissionPromptedKey) === 'true') {
      console.warn('OneSignal: Already prompted previously');
      return false;
    }

    const oneSignal = (typeof OneSignal !== 'undefined') ? OneSignal : (typeof window !== 'undefined' && (window as any).OneSignal) ? (window as any).OneSignal : null;
    if (!oneSignal?.Notifications) {
      console.warn('OneSignal: SDK not available for permission request');
      return false;
    }

    try {
      const hasPermission = await oneSignal.Notifications.getPermissionAsync();
      if (hasPermission) {
        console.warn('OneSignal: Already has permission');
        return true;
      }

      const canRequest = await oneSignal.Notifications.canRequestPermission();
      if (!canRequest) {
        console.warn('OneSignal: Cannot request permission');
        return false;
      }

      this.hasPromptedThisSession = true;
      localStorage.setItem(this.permissionPromptedKey, 'true');

      const accepted = await oneSignal.Notifications.requestPermission(true);
      console.warn('OneSignal: Permission request result:', accepted);

      if (accepted) {
        setTimeout(() => this.logSubscriptionInfo(oneSignal), 2000);
      }
      this.afterPromptAnswer();
      return accepted;
    } catch (error) {
      console.error('OneSignal: Error requesting permission:', error);
      return false;
    }
  }

  async alreadyAuthenticatedInit() {
    if(!this.auth.isAuthenticated()) {
      return;
    }
    if (this.userSetupInProgress) {
      console.warn('OneSignal: User setup already in progress, skipping');
      return;
    }
    try {
      const meData = await this.me.load();
      const userId = meData.current_user.id;
      if (this.currentUserId === userId) {
        console.warn('OneSignal: User already set up for this user ID, skipping');
        return;
      }
      this.setUpNotificationUser(userId, meData.customer_account.email);
    } catch (error) {
      console.error('OneSignal: Error in alreadyAuthenticatedInit:', error);
    }
  }

  setUpNotificationUser(userId, userEmail, retry = 0) {
    console.warn('OneSignal: setUpNotificationUser called', { userId, userEmail, retry });

    if (this.userSetupInProgress && this.currentUserId === userId) {
      console.warn('OneSignal: User setup already in progress for user:', userId);
      return;
    }

    if (!this.isInitialized) {
      console.warn('OneSignal: Cannot set up user - SDK not initialized. Retry:', retry);
      if (retry < 10) {
        const thirtySeconds = 30000;
        console.warn('OneSignal: Will retry user setup in 30 seconds');
        setTimeout(() => {
          this.setUpNotificationUser(userId, userEmail, (retry + 1));
        }, thirtySeconds);
      }
      return;
    }

    const oneSignal = (typeof OneSignal !== 'undefined') ? OneSignal : (typeof window !== 'undefined' && (window as any).OneSignal) ? (window as any).OneSignal : null;

    if (!oneSignal) {
      console.warn('OneSignal: Cannot set up user - SDK not available. Retry:', retry);
      if (retry < 10) {
        const thirtySeconds = 30000;
        console.warn('OneSignal: Will retry user setup in 30 seconds');
        setTimeout(() => {
          this.setUpNotificationUser(userId, userEmail, (retry + 1));
        }, thirtySeconds);
      }
      return;
    }

    this.userSetupInProgress = true;
    this.currentUserId = userId;

    try {
      if (typeof oneSignal.User === 'undefined') {
        console.error('OneSignal: User API not available');
        this.userSetupInProgress = false;
        return;
      }

      console.warn('OneSignal: Adding user tags...');
      oneSignal.User.addTags({
        email: userEmail,
        external_id: userId,
        env: this.envTag
      });
      console.warn('OneSignal: Tags added:', { email: userEmail, external_id: userId, env: this.envTag });

      if (typeof oneSignal.login === 'function') {
        console.warn('OneSignal: Calling login with userId:', userId);
        oneSignal.login(`${userId}`);
      } else {
        console.warn('OneSignal: login function not available');
      }

      // Note: addEmail is disabled because it causes "Unable to parse response" errors
      // when email subscription isn't properly configured in OneSignal dashboard.
      // The email is still stored in tags for segmentation purposes.
      // To re-enable, uncomment the following:
      // console.warn('OneSignal: Adding email:', userEmail);
      // oneSignal.User.addEmail(userEmail);

      console.warn('OneSignal: User set up successfully for:', userId);
      if (!this.subscriptionInfoLogged) {
        setTimeout(() => {
          this.logSubscriptionInfo(oneSignal);
        }, 2000);
      }
      this.userSetupInProgress = false;
    } catch (err) {
      console.error('OneSignal: Error setting up user:', err);
      console.error('OneSignal: Error details:', JSON.stringify(err));
      this.userSetupInProgress = false;
      const thirtySeconds = 30000;
      if (retry < 10) {
        console.warn('OneSignal: Will retry user setup in 30 seconds');
        setTimeout(() => {
          this.setUpNotificationUser(userId, userEmail, (retry + 1));
        }, thirtySeconds);
      }
    }
  }

  private async logSubscriptionInfo(oneSignal: any) {
    console.warn('OneSignal: logSubscriptionInfo called');

    if (this.subscriptionInfoLogged) {
      console.warn('OneSignal: Subscription info already logged');
      return;
    }

    try {
      // Log all available user info
      console.warn('OneSignal: User API available:', typeof oneSignal.User !== 'undefined');
      console.warn('OneSignal: pushSubscription available:', typeof oneSignal.User?.pushSubscription !== 'undefined');

      if (typeof oneSignal.User !== 'undefined') {
        // Try to get onesignalId
        try {
          const onesignalId = oneSignal.User.onesignalId;
          console.warn('OneSignal: OneSignal User ID:', onesignalId || 'Not set');
        } catch (e) {
          console.warn('OneSignal: Could not get onesignalId:', e);
        }

        // Try to get externalId
        try {
          const externalId = oneSignal.User.externalId;
          console.warn('OneSignal: External User ID:', externalId || 'Not set');
        } catch (e) {
          console.warn('OneSignal: Could not get externalId:', e);
        }

        if (typeof oneSignal.User.pushSubscription !== 'undefined') {
          try {
            const subscriptionId = await oneSignal.User.pushSubscription.getIdAsync();
            const subscriptionToken = await oneSignal.User.pushSubscription.getTokenAsync();
            const optedIn = oneSignal.User.pushSubscription.optedIn;

            console.warn('OneSignal: Push Subscription ID:', subscriptionId || 'Not available');
            console.warn('OneSignal: Push Token:', subscriptionToken ? `${subscriptionToken.substring(0, 30)}...` : 'Not available');
            console.warn('OneSignal: Opted In:', optedIn);

            if (subscriptionId) {
              this.subscriptionInfoLogged = true;
            } else {
              console.warn('OneSignal: WARNING - No subscription ID. Device may not receive push notifications.');
            }

            if (!subscriptionToken) {
              console.warn('OneSignal: WARNING - No push token. Check platform push configuration.');
            }
          } catch (e) {
            console.warn('OneSignal: Could not retrieve subscription info via User.pushSubscription:', e);
          }
        }
      } else if (typeof oneSignal.Notifications !== 'undefined') {
        try {
          if (typeof oneSignal.Notifications.getIdAsync === 'function') {
            const subscriptionId = await oneSignal.Notifications.getIdAsync();
            const subscriptionToken = await oneSignal.Notifications.getTokenAsync();
            console.warn('OneSignal: Subscription ID (Notifications API):', subscriptionId || 'Not available');
            console.warn('OneSignal: Subscription Token (Notifications API):', subscriptionToken ? `${subscriptionToken.substring(0, 30)}...` : 'Not available');
            if (subscriptionId) {
              this.subscriptionInfoLogged = true;
            }
          }
        } catch (e) {
          console.warn('OneSignal: Could not retrieve subscription info via Notifications API:', e);
        }
      }
    } catch (error) {
      console.warn('OneSignal: Error getting subscription info:', error);
    }
  }

  afterPromptAnswer() {
    // Tag is now set in setUpNotificationUser
    this.alreadyAuthenticatedInit();
  }

  notificationOpened(openedEvent: any) {
    const pageData = this.parseNotificationData(openedEvent);
    this.notificationsRedirect.redirectToPage(pageData)
    this.afterLoginRedirect.redirectTo = { name: pageData.name , data: pageData.data };
  }

  parseNotificationData(notificationData: any): NotificationData {
    try {
      // In the new SDK, the structure might be slightly different
      const notification = notificationData.notification || notificationData;
      const additionalData = notification.additionalData || notification.data || {};
      return {
        name: additionalData.name || '',
        data: {
          ...additionalData.data,
          notification: {
            title: notification.title,
            body: notification.body,
          }
        }
      }
    }
    catch (_err) {
      return {
        name: '',
        data: {}
      }
    }
  }
}

export interface NotificationData {
  name: string,
  data: any
  [key: string]: any
}
